import { TrendingUp } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

/**
 * Client-side ROI estimator with slider inputs. Shows how much revenue a
 * business loses to missed calls and what an AI receptionist could recover.
 * Pure presentation math — no network, no PII, no analytics. Each slider is
 * labelled + aria-described and exposes its live value; no motion is used
 * (reduced-motion-safe). Assumptions are stated inline so the number is honest.
 */

const WORKING_DAYS_PER_MONTH = 22;
const STARTER_PRICE_USD = 39;

const usd = (n: number): string =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

interface Field {
  key: 'callsPerDay' | 'missedPct' | 'customerValue' | 'conversionPct';
  label: string;
  min: number;
  max: number;
  step: number;
  format: (n: number) => string;
}

const FIELDS: Field[] = [
  {
    key: 'callsPerDay',
    label: 'Calls per day',
    min: 0,
    max: 200,
    step: 1,
    format: (n) => `${n}`,
  },
  {
    key: 'missedPct',
    label: 'Calls currently missed',
    min: 0,
    max: 100,
    step: 1,
    format: (n) => `${n}%`,
  },
  {
    key: 'customerValue',
    label: 'Avg. customer value',
    min: 0,
    max: 5000,
    step: 50,
    format: (n) => usd(n),
  },
  {
    key: 'conversionPct',
    label: 'Lead → customer rate',
    min: 0,
    max: 100,
    step: 1,
    format: (n) => `${n}%`,
  },
];

type Inputs = Record<Field['key'], number>;

const DEFAULTS: Inputs = {
  callsPerDay: 30,
  missedPct: 25,
  customerValue: 500,
  conversionPct: 30,
};

export function SavingsCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<Inputs>(DEFAULTS);
  const baseId = useId();

  const { missedPerMonth, recoverableRevenue, netUpside } = useMemo(() => {
    const missed = inputs.callsPerDay * (inputs.missedPct / 100) * WORKING_DAYS_PER_MONTH;
    const revenue = missed * (inputs.conversionPct / 100) * inputs.customerValue;
    return {
      missedPerMonth: missed,
      recoverableRevenue: revenue,
      netUpside: revenue - STARTER_PRICE_USD,
    };
  }, [inputs]);

  const setField = (key: Field['key'], raw: string): void => {
    const n = Number(raw);
    setInputs((prev) => ({ ...prev, [key]: Number.isFinite(n) && n >= 0 ? n : 0 }));
  };

  return (
    <section className="container py-14 sm:py-20">
      <div className="mx-auto max-w-2xl text-center">
        <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
          What are missed calls costing you?
        </h2>
        <p className="mt-4 text-muted-foreground">
          Drag the sliders to match your business and see the revenue an always-on
          AI receptionist could recover.
        </p>
      </div>

      <div className="mx-auto mt-12 grid max-w-5xl items-stretch gap-6 lg:grid-cols-5">
        {/* Inputs */}
        <form
          className="rounded-2xl border border-border bg-card p-6 shadow-sm sm:p-8 lg:col-span-3"
          aria-label="Savings calculator inputs"
        >
          <div className="space-y-7">
            {FIELDS.map((f) => {
              const id = `${baseId}-${f.key}`;
              const value = inputs[f.key];
              return (
                <div key={f.key}>
                  <div className="flex items-baseline justify-between">
                    <label htmlFor={id} className="text-sm font-medium">
                      {f.label}
                    </label>
                    <span className="rounded-md bg-[var(--brand-accent)]/10 px-2 py-0.5 text-sm font-semibold tabular-nums text-[var(--brand-accent)]">
                      {f.format(value)}
                    </span>
                  </div>
                  <input
                    id={id}
                    type="range"
                    min={f.min}
                    max={f.max}
                    step={f.step}
                    value={value}
                    aria-valuetext={f.format(value)}
                    onChange={(e) => setField(f.key, e.target.value)}
                    className="mt-3 w-full accent-[var(--brand-accent)]"
                  />
                  <div className="mt-1 flex justify-between text-xs text-muted-foreground">
                    <span>{f.format(f.min)}</span>
                    <span>{f.format(f.max)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </form>

        {/* Results */}
        <div
          className="flex flex-col justify-center gap-6 rounded-2xl bg-gradient-to-br from-[var(--brand-accent)] to-emerald-500 p-6 text-white shadow-lg sm:p-8 lg:col-span-2"
          aria-live="polite"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-white/80">
            <TrendingUp className="h-4 w-4" aria-hidden="true" />
            Your estimate
          </div>
          <Result label="Missed calls / month" value={Math.round(missedPerMonth).toLocaleString()} />
          <div className="h-px bg-white/20" />
          <Result label="Recoverable revenue / month" value={usd(recoverableRevenue)} big />
          <Result label={`Net upside after Starter ($${STARTER_PRICE_USD}/mo)`} value={usd(netUpside)} />
          <p className="text-xs leading-relaxed text-white/70">
            Estimates only. Assumes {WORKING_DAYS_PER_MONTH} working days/month and that
            your inputs are representative. Not a guarantee of results.
          </p>
        </div>
      </div>
    </section>
  );
}

function Result({
  label,
  value,
  big,
}: {
  label: string;
  value: string;
  big?: boolean;
}): JSX.Element {
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-white/70">{label}</p>
      <p className={big ? 'mt-1 text-4xl font-bold tabular-nums' : 'mt-0.5 text-2xl font-semibold tabular-nums'}>
        {value}
      </p>
    </div>
  );
}
